/**
 * Wave 27.0 — Expense Review Queue (admin / manager / owner).
 *
 * Inbox of OCR'd receipts awaiting confirmation. Each card shows the
 * extracted fields with confidence, the original image preview, and
 * lets the reviewer:
 *   • Confirm/edit supplier, date, total, line items
 *   • Pick project + expense category (inline-add new category)
 *   • Approve  → cost lands on project P&L + receipt filed to supplier
 *   • Reject   → with reason
 *   • Rescan   → re-run OCR
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Loader2, AlertTriangle, Check, X, RefreshCw, Search, Plus, ChevronDown,
  Receipt as ReceiptIcon, Camera, Inbox, ZoomIn, ExternalLink, Tag,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";
import { uploadUrl } from "../lib/uploads";
import { EntityPicker } from "./ProjectNew";
import DatePickerInput from "../components/DatePickerInput";
import { ExpenseStatusPill } from "./ProjectDetail";

const STATUS_TABS = [
  { key: "pending", label: "Pending review", color: "#F59E0B" },
  { key: "ocr_pending", label: "OCR running", color: "#0EA5E9" },
  { key: "ocr_failed", label: "OCR failed", color: "#F43F5E" },
  { key: "approved", label: "Approved", color: "#10B981" },
  { key: "rejected", label: "Rejected", color: "#647087" },
];

const fmtMoney = (n, ccy = "ZAR") =>
  n == null ? "—" : `${ccy} ${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ExpenseReviewQueue() {
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const canApprove = role === "owner" || role === "admin" || role === "manager";
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "pending");
  const [projectFilter, setProjectFilter] = useState(searchParams.get("project") || "");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});

  const load = useCallback(async (opts) => {
    const silent = !!opts?.silent;
    if (!silent) setLoading(true);
    try {
      const params = { status: tab, limit: 50 };
      if (projectFilter) params.project_id = projectFilter;
      const [{ data }, { data: s }] = await Promise.all([
        api.get("/expenses", { params }),
        api.get("/expenses/_stats"),
      ]);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setStats(s);
    } catch {
      if (!silent) toast.error("Failed to load expense queue.");
    } finally { if (!silent) setLoading(false); }
  }, [tab, projectFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const next = { tab };
    if (projectFilter) next.project = projectFilter;
    setSearchParams(next, { replace: true });
  }, [tab, projectFilter, setSearchParams]);

  // Poll the OCR-pending tab every 6s for live updates — silent so the
  // queue doesn't blank while admin is mid-scroll (iter 173, Dirk).
  useEffect(() => {
    if (tab !== "ocr_pending") return;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") load({ silent: true });
    }, 6000);
    return () => clearInterval(t);
  }, [tab, load]);

  if (!canApprove) {
    return (
      <div className="hv-card p-8 text-center text-[#a0aabf]" data-testid="expenses-review-forbidden">
        <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-400" />
        <div>Expense review is restricted to admins and managers.</div>
        <Link to="/camera?expense=1" className="hv-btn hv-btn-primary text-xs mt-3 inline-flex">
          <Camera className="w-3.5 h-3.5" /> Capture a receipt
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="expenses-review-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold inline-flex items-center gap-2">
            <ReceiptIcon className="w-6 h-6 text-[#F59E0B]" /> Expense review
          </h1>
          <p className="text-xs text-[#647087] mt-0.5">
            {stats.pending_review || 0} awaiting review · {stats.ocr_pending || 0} still OCRing · {stats.ocr_failed || 0} failed.
          </p>
        </div>
        <Link to="/camera?expense=1" className="hv-btn hv-btn-primary text-xs" data-testid="expenses-capture">
          <Camera className="w-3.5 h-3.5" /> Capture receipt
        </Link>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {STATUS_TABS.map((s) => (
          <button
            key={s.key}
            onClick={() => setTab(s.key)}
            className={`text-[11px] px-2.5 py-1 rounded-full inline-flex items-center gap-1 border transition-colors ${
              tab === s.key ? "border-transparent text-[#0a0b0e] font-semibold" : "border-white/15 text-[#a0aabf] hover:bg-white/5"
            }`}
            style={tab === s.key ? { background: s.color } : {}}
            data-testid={`exp-tab-${s.key}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="hv-card p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#1FB6C1]" /></div>
      ) : items.length === 0 ? (
        <div className="hv-card p-10 text-center text-[#647087]" data-testid="exp-empty">
          <Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <div>No {STATUS_TABS.find((t) => t.key === tab)?.label.toLowerCase()} receipts.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((e) => (
            <ExpenseCard key={e.id} expense={e} onChange={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function ExpenseCard({ expense: initial, onChange }) {
  const [e, setE] = useState(initial);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => { setE(initial); }, [initial]);

  useEffect(() => {
    api.get("/expense-categories").then(({ data }) => setCategories(data.items || []));
  }, []);

  const upd = (k, v) => setE((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        project_id: e.project_id || null,
        supplier_name: e.supplier_name || null,
        supplier_doc_number: e.supplier_doc_number || null,
        date: e.date || null,
        currency: e.currency || null,
        line_items: e.line_items,
        subtotal: e.subtotal == null ? null : Number(e.subtotal),
        tax: e.tax == null ? null : Number(e.tax),
        total: e.total == null ? null : Number(e.total),
        category_id: e.category_id || null,
        notes: e.notes || null,
      };
      const { data } = await api.patch(`/expenses/${e.id}`, payload);
      setE(data);
      setEditing(false);
      toast.success("Saved.");
      onChange?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    if (!e.project_id) { toast.error("Pick a project first."); return; }
    if (!e.category_id) { toast.error("Pick a category first."); return; }
    setBusy(true);
    try {
      await api.post(`/expenses/${e.id}/approve`);
      toast.success("Approved.");
      onChange?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Approve failed."); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    const reason = window.prompt("Reason for rejection (optional)?") || "";
    setBusy(true);
    try {
      await api.post(`/expenses/${e.id}/reject`, { reason });
      toast.success("Rejected.");
      onChange?.();
    } catch { toast.error("Reject failed."); }
    finally { setBusy(false); }
  };

  const rescan = async () => {
    setBusy(true);
    try {
      await api.post(`/expenses/${e.id}/rescan`);
      toast.info("Rescan started — refresh in a few seconds.");
      onChange?.();
    } catch { toast.error("Rescan failed."); }
    finally { setBusy(false); }
  };

  const confColor = e.confidence == null ? "#647087" : e.confidence < 0.6 ? "#F43F5E" : e.confidence < 0.85 ? "#F59E0B" : "#10B981";

  return (
    <div className="hv-card !p-3" data-testid={`exp-card-${e.id}`}>
      <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-3">
        {/* Image preview.
            Iter 427.p49 — Manual expenses (upload_id=null) previously
            rendered `<img src="">` which triggers a broken-image icon
            and, worse, a fetch to the current page. Now: render a
            receipt-icon placeholder when there's no upload. */}
        {e.upload_id ? (
          <a
            href={uploadUrl(e.upload_id)}
            target="_blank"
            rel="noreferrer"
            className="block aspect-square rounded overflow-hidden bg-black/40 border border-white/10 hover:border-[#1FB6C1]/50 relative group"
            data-testid={`exp-preview-${e.id}`}
          >
            <img
              src={uploadUrl(e.upload_id)}
              alt="Receipt"
              className="w-full h-full object-cover"
              onError={(ev) => {
                // If the underlying upload is missing or corrupt,
                // swap in an inline placeholder so the reviewer sees
                // context (icon + "unavailable") instead of a system
                // broken-image glyph.
                const img = ev.currentTarget;
                img.style.display = "none";
                if (img.parentElement && !img.parentElement.querySelector(".exp-preview-fallback")) {
                  const div = document.createElement("div");
                  div.className = "exp-preview-fallback absolute inset-0 flex flex-col items-center justify-center gap-1 text-[#647087] bg-black/40";
                  div.innerHTML = '<div class="text-[10px] uppercase tracking-wider">Image missing</div>';
                  img.parentElement.appendChild(div);
                }
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
              <ZoomIn className="w-6 h-6 text-white" />
            </div>
          </a>
        ) : (
          <div
            className="aspect-square rounded overflow-hidden bg-black/40 border border-white/10 flex flex-col items-center justify-center gap-1 text-[#647087]"
            data-testid={`exp-preview-manual-${e.id}`}
            title="Manual expense — no receipt image"
          >
            <ReceiptIcon className="w-8 h-8 text-[#F59E0B]/40" />
            <div className="text-[9px] uppercase tracking-wider">Manual entry</div>
            <div className="text-[9px] text-[#647087]/70">No image</div>
          </div>
        )}

        {/* Fields */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <ExpenseStatusPill status={e.status} confidence={e.confidence} />
              {e.confidence != null && (
                <span className="text-[10px] font-mono" style={{ color: confColor }}>
                  conf {Math.round(e.confidence * 100)}%
                </span>
              )}
              <span className="text-[10px] text-[#647087]">by {e.captured_by_name}</span>
            </div>
            <div className="flex items-center gap-1">
              {e.status === "ocr_failed" && (
                <button onClick={rescan} disabled={busy} className="hv-btn hv-btn-ghost text-[11px]" data-testid={`exp-rescan-${e.id}`}>
                  <RefreshCw className="w-3 h-3" /> Rescan
                </button>
              )}
              {e.status === "pending" && !editing && (
                <button onClick={() => setEditing(true)} className="hv-btn hv-btn-ghost text-[11px]" data-testid={`exp-edit-${e.id}`}>Edit</button>
              )}
            </div>
          </div>

          {e.status === "ocr_pending" && (
            <div className="text-[11px] text-[#a0aabf] italic flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> GPT-5.2 Vision is reading the receipt…
            </div>
          )}
          {e.status === "ocr_failed" && (
            <div className="text-[11px] text-rose-400">{e.ocr_error || "OCR couldn't parse this receipt — try Rescan or capture a sharper photo."}</div>
          )}

          {/* Editable fields */}
          {editing ? (
            <EditFields e={e} upd={upd} categories={categories} onCategoryCreated={(c) => setCategories((p) => [...p, c])} />
          ) : (
            <ReadFields e={e} categories={categories} />
          )}

          {/* Project + Category required pickers (always visible while pending) */}
          {(e.status === "pending" || e.status === "approved") && !editing && (
            <>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Field label="Project">
                  <EntityPicker
                    kind="project"
                    value={e.project_id ? { id: e.project_id, label: e.project_short_id || e.project_id?.slice(0, 8) + "…" } : null}
                    onChange={async (p) => {
                      upd("project_id", p?.id || null);
                      upd("project_short_id", p?.label || null);
                      // Iter 427.p49 — changing project clears any
                      // budget-line linkage since the line belongs to
                      // the old project's SO/quote and would be
                      // orphaned.
                      upd("budget_source_doc_id", null);
                      upd("budget_source_line_index", null);
                      upd("budget_source_description", null);
                      if (p?.id && e.status !== "approved") {
                        await api.patch(`/expenses/${e.id}`, {
                          project_id: p.id,
                          budget_source_doc_id: "",  // sentinel = clear
                        });
                        onChange?.();
                      }
                    }}
                    placeholder="Pick a project"
                    testId={`exp-pick-project-${e.id}`}
                  />
                </Field>
                <Field label="Category">
                  <CategoryPicker
                    value={e.category_id}
                    categories={categories}
                    onCategoryCreated={(c) => setCategories((p) => [...p, c])}
                    onChange={async (cid, cname) => {
                      upd("category_id", cid);
                      upd("category_name", cname);
                      if (cid && e.status !== "approved") {
                        await api.patch(`/expenses/${e.id}`, { category_id: cid });
                        onChange?.();
                      }
                    }}
                    testId={`exp-pick-category-${e.id}`}
                  />
                </Field>
              </div>
              {/* Iter 427.p49 — Budget-line matcher. Only meaningful
                  once a project is set; the picker pulls the project's
                  live budget-breakdown lines and lets the reviewer
                  attach this receipt to one specific line so the
                  Profitability tab can roll spend up per line.
                  Optional — leaving it unmatched keeps the current
                  "goes to project general" behaviour. */}
              {e.project_id && (
                <div className="mt-2">
                  <Field label="Budget line (optional — links this receipt to a specific quoted line)">
                    <BudgetLinePicker
                      projectId={e.project_id}
                      value={e.budget_source_doc_id && e.budget_source_line_index != null ? {
                        doc_id: e.budget_source_doc_id,
                        line_index: e.budget_source_line_index,
                        description: e.budget_source_description,
                      } : null}
                      readOnly={e.status === "approved"}
                      onChange={async (pick) => {
                        if (pick) {
                          upd("budget_source_doc_id", pick.doc_id);
                          upd("budget_source_line_index", pick.line_index);
                          upd("budget_source_description", pick.description);
                          if (e.status !== "approved") {
                            await api.patch(`/expenses/${e.id}`, {
                              budget_source_doc_id: pick.doc_id,
                              budget_source_line_index: pick.line_index,
                              budget_source_description: pick.description,
                            });
                            onChange?.();
                          }
                        } else {
                          upd("budget_source_doc_id", null);
                          upd("budget_source_line_index", null);
                          upd("budget_source_description", null);
                          if (e.status !== "approved") {
                            await api.patch(`/expenses/${e.id}`, {
                              budget_source_doc_id: "",  // sentinel = clear
                            });
                            onChange?.();
                          }
                        }
                      }}
                      testId={`exp-pick-budget-line-${e.id}`}
                    />
                  </Field>
                </div>
              )}
              {/* Phase A — Margin Protector */}
              {e.status === "pending" && e.project_id && (e.total > 0) && (
                <BudgetProtector projectId={e.project_id} additionalCost={e.total} testIdPrefix={`exp-protector-${e.id}`} />
              )}
            </>
          )}

          {/* Action bar */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5 mt-2">
            {editing ? (
              <>
                <button onClick={() => { setEditing(false); setE(initial); }} className="hv-btn hv-btn-ghost text-xs">Cancel</button>
                <button onClick={save} disabled={busy} className="hv-btn hv-btn-primary text-xs disabled:opacity-50" data-testid={`exp-save-${e.id}`}>
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save
                </button>
              </>
            ) : e.status === "pending" ? (
              <>
                <button onClick={reject} disabled={busy} className="hv-btn hv-btn-ghost text-xs text-rose-400" data-testid={`exp-reject-${e.id}`}>
                  <X className="w-3.5 h-3.5" /> Reject
                </button>
                <button
                  onClick={approve}
                  disabled={busy || !e.project_id || !e.category_id || !(e.total > 0)}
                  className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
                  data-testid={`exp-approve-${e.id}`}
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Approve
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-[#647087] mb-0.5">{label}</div>
      {children}
    </div>
  );
}

function ReadFields({ e }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
      <Stat label="Supplier" value={e.supplier_name || "—"} />
      <Stat label="Doc #" value={e.supplier_doc_number || "—"} />
      <Stat label="Date" value={e.date || "—"} />
      <Stat label="Total" value={fmtMoney(e.total, e.currency || "ZAR")} bold />
      {/* Iter 427.p49 — Show the linked budget line right here so the
          reviewer can see at-a-glance which quote/SO line this
          receipt maps back to. Full picker lives below in the
          project/category grid. */}
      {e.budget_source_doc_id && e.budget_source_line_index != null && (
        <div className="col-span-2 sm:col-span-4">
          <div className="text-[9px] uppercase tracking-wider text-[#647087] mb-0.5">Budget line</div>
          <div
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-[#1FB6C1]/40 bg-[#1FB6C1]/10 text-[11px] text-[#1FB6C1]"
            data-testid={`exp-budget-line-chip-${e.id}`}
          >
            <Tag className="w-3 h-3" />
            <span className="truncate max-w-[420px]">
              {e.budget_source_description || `line ${e.budget_source_line_index + 1}`}
            </span>
          </div>
        </div>
      )}
      {e.line_items?.length > 0 && (
        <div className="col-span-2 sm:col-span-4 mt-1">
          <div className="text-[9px] uppercase tracking-wider text-[#647087] mb-1">Line items ({e.line_items.length})</div>
          <ul className="space-y-0.5 max-h-32 overflow-y-auto">
            {e.line_items.slice(0, 8).map((li, i) => (
              <li key={i} className="text-[11px] flex justify-between gap-2 px-1 py-0.5 hover:bg-white/[0.03] rounded">
                <span className="text-[#a0aabf] truncate">{li.qty != null ? `${li.qty}× ` : ""}{li.description}</span>
                <span className="font-mono text-white">{Number(li.total || 0).toFixed(2)}</span>
              </li>
            ))}
            {e.line_items.length > 8 && <li className="text-[10px] text-[#647087] italic">+{e.line_items.length - 8} more…</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

function EditFields({ e, upd }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
      <Field label="Supplier">
        <input value={e.supplier_name || ""} onChange={(ev) => upd("supplier_name", ev.target.value)} className="hv-input text-xs w-full" data-testid={`exp-supplier-${e.id}`} />
      </Field>
      <Field label="Doc #">
        <input value={e.supplier_doc_number || ""} onChange={(ev) => upd("supplier_doc_number", ev.target.value)} className="hv-input text-xs w-full" />
      </Field>
      <Field label="Date">
        <DatePickerInput value={e.date || ""} onChange={(v) => upd("date", v)} />
      </Field>
      <Field label="Currency">
        <select value={e.currency || "ZAR"} onChange={(ev) => upd("currency", ev.target.value)} className="hv-input text-xs w-full">
          {["ZAR", "USD", "EUR", "GBP"].map((c) => <option key={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Subtotal">
        <input type="number" step="0.01" value={e.subtotal ?? ""} onChange={(ev) => upd("subtotal", ev.target.value)} className="hv-input text-xs w-full" />
      </Field>
      <Field label="Tax">
        <input type="number" step="0.01" value={e.tax ?? ""} onChange={(ev) => upd("tax", ev.target.value)} className="hv-input text-xs w-full" />
      </Field>
      <Field label="Total">
        <input type="number" step="0.01" value={e.total ?? ""} onChange={(ev) => upd("total", ev.target.value)} className="hv-input text-xs w-full" data-testid={`exp-total-${e.id}`} />
      </Field>
      <div />
      <div className="col-span-2 sm:col-span-4">
        <Field label="Notes">
          <input value={e.notes || ""} onChange={(ev) => upd("notes", ev.target.value)} className="hv-input text-xs w-full" />
        </Field>
      </div>
    </div>
  );
}

function Stat({ label, value, bold }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-[#647087]">{label}</div>
      <div className={`text-white ${bold ? "font-mono text-sm" : ""}`}>{value}</div>
    </div>
  );
}

/** Inline-addable category picker. */
function CategoryPicker({ value, categories, onChange, onCategoryCreated, testId }) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const current = categories.find((c) => c.id === value);

  const create = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/expense-categories", { name: newName.trim() });
      onCategoryCreated?.(data);
      onChange(data.id, data.name);
      setCreating(false);
      setNewName("");
      setOpen(false);
      toast.success(`Added "${data.name}".`);
    } catch { toast.error("Create failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative">
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="hv-input text-xs flex-1 flex items-center justify-between gap-2"
          data-testid={testId}
        >
          <span className={current ? "text-white truncate" : "text-[#647087]"}>{current?.name || "Pick category"}</span>
          <ChevronDown className="w-3 h-3 text-[#647087]" />
        </button>
        <button
          type="button"
          onClick={() => { setOpen(true); setCreating(true); }}
          className="hv-btn hv-btn-ghost text-[11px] !px-2 shrink-0"
          title="Add new category"
          aria-label="Add new category"
          data-testid="exp-new-category-trigger"
        >
          <Plus className="w-3 h-3" />
        </button>
      </div>
      {open && (
        <div className="absolute z-30 mt-1 left-0 right-0 bg-[#0e1015] border border-white/10 rounded shadow-xl max-h-72 overflow-y-auto p-1.5 min-w-[200px]">
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => { onChange(c.id, c.name); setOpen(false); }}
              className="w-full text-left text-xs px-2 py-1 rounded hover:bg-white/[0.04] flex items-center gap-2"
              data-testid={`exp-category-${c.slug}`}
            >
              <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />
              {c.name}
            </button>
          ))}
          <div className="border-t border-white/5 mt-1 pt-1">
            {creating ? (
              <div className="flex items-center gap-1 p-1">
                <input
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") create(); }}
                  placeholder="New category…"
                  className="hv-input text-xs flex-1"
                  data-testid="exp-new-category-input"
                />
                <button onClick={create} disabled={busy} className="hv-btn hv-btn-primary text-[10px] disabled:opacity-50" data-testid="exp-new-category-save">
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                </button>
                <button onClick={() => { setCreating(false); setNewName(""); }} className="text-[#647087] hover:text-white" aria-label="Cancel">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="w-full text-left text-xs px-2 py-1 rounded hover:bg-white/[0.04] inline-flex items-center gap-1.5 text-[#1FB6C1]"
              >
                <Plus className="w-3 h-3" /> Add new category
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/** Phase A — Margin Protector. Pre-flight call to /budget-check that
 *  warns the reviewer BEFORE they approve a receipt that pushes the
 *  project past 70 / 90 / 100 %. Soft warning only — does NOT block
 *  the Approve button.
 */
function BudgetProtector({ projectId, additionalCost, testIdPrefix }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!projectId || !additionalCost) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    api.get(`/projects/${projectId}/budget-check`, { params: { additional_cost: additionalCost } })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, additionalCost]);
  if (loading) return null;
  if (!data || data.level === "ok") return null;
  const palette = {
    caution: { fg: "#F59E0B", bg: "rgba(245,158,11,0.08)", border: "rgba(245,158,11,0.35)" },
    warning: { fg: "#F43F5E", bg: "rgba(244,63,94,0.08)", border: "rgba(244,63,94,0.4)" },
    over:    { fg: "#DC2626", bg: "rgba(220,38,38,0.12)", border: "rgba(220,38,38,0.5)" },
  }[data.level] || { fg: "#647087", bg: "transparent", border: "rgba(255,255,255,0.1)" };
  return (
    <div
      className="mt-2 px-2 py-1.5 rounded text-[11px] inline-flex items-start gap-1.5 w-full"
      style={{ background: palette.bg, border: `1px solid ${palette.border}`, color: palette.fg }}
      data-testid={testIdPrefix}
    >
      <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
      <span>
        <strong>Margin protector:</strong> {data.message}
        <span className="text-[#a0aabf] ml-1">(approval still allowed)</span>
      </span>
    </div>
  );
}


/** Iter 427.p49 — Budget-line picker.
 * Fetches the current project's `/budget-breakdown` (revenue-side
 * lines: quotes, sales orders, invoices) and lets the reviewer link
 * this receipt to ONE specific line. The natural key is
 * (doc_id, line_index); we also snapshot the description for readable
 * display.
 *
 * Optional field — leaving it unset keeps the current "receipt rolls
 * up under project general" behaviour.
 */
function BudgetLinePicker({ projectId, value, onChange, readOnly, testId }) {
  const [lines, setLines] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!projectId) { setLines(null); return; }
    let cancelled = false;
    setErr("");
    api.get(`/projects/${projectId}/budget-breakdown`)
      .then(({ data }) => {
        if (cancelled) return;
        // Endpoint returns { lines: [{doc_id, line_index, description, sku, ...}] }.
        setLines(Array.isArray(data?.lines) ? data.lines : []);
      })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || "Could not load budget lines"); });
    return () => { cancelled = true; };
  }, [projectId]);

  const currentLabel = value
    ? (value.description || `${value.doc_id?.slice(0, 8)}… · line ${value.line_index + 1}`)
    : null;

  const filtered = (lines || []).filter((ln) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (
      (ln.description || "").toLowerCase().includes(s) ||
      (ln.sku || "").toLowerCase().includes(s) ||
      (ln.doc_number || "").toLowerCase().includes(s)
    );
  });

  return (
    <div className="relative">
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => !readOnly && setOpen((v) => !v)}
          disabled={readOnly}
          className="hv-input text-xs flex-1 flex items-center justify-between gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid={testId}
          title={currentLabel || "Match to a quoted / ordered / invoiced line"}
        >
          <span className={value ? "text-white truncate" : "text-[#647087] truncate"}>
            {currentLabel || "Pick budget line (optional)"}
          </span>
          <ChevronDown className="w-3 h-3 text-[#647087] shrink-0" />
        </button>
        {value && !readOnly && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="hv-btn hv-btn-ghost text-[11px] !px-2 shrink-0"
            title="Unlink budget line"
            aria-label="Unlink budget line"
            data-testid={`${testId}-clear`}
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
      {open && !readOnly && (
        <div
          className="absolute z-30 mt-1 left-0 right-0 bg-[#0e1015] border border-white/10 rounded shadow-xl max-h-72 overflow-y-auto p-1.5 min-w-[240px]"
          data-testid={`${testId}-list`}
        >
          <div className="p-1">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search description / SKU / doc #…"
              className="hv-input text-xs w-full"
              data-testid={`${testId}-search`}
            />
          </div>
          {lines === null && (
            <div className="p-2 text-[11px] text-[#647087] inline-flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading budget lines…
            </div>
          )}
          {err && (
            <div className="p-2 text-[11px] text-rose-400">{err}</div>
          )}
          {lines && lines.length === 0 && (
            <div className="p-2 text-[11px] text-[#647087] italic">
              This project has no budget lines yet. Add a quote or sales order first.
            </div>
          )}
          {filtered.map((ln) => {
            const isSel = value && value.doc_id === ln.doc_id && value.line_index === ln.line_index;
            return (
              <button
                key={`${ln.doc_id}::${ln.line_index}`}
                type="button"
                onClick={() => {
                  onChange({
                    doc_id: ln.doc_id,
                    line_index: ln.line_index,
                    description: ln.description || ln.sku || "",
                  });
                  setOpen(false);
                }}
                className={`w-full text-left text-xs px-2 py-1.5 rounded flex flex-col gap-0.5 ${
                  isSel ? "bg-[#1FB6C1]/15 border border-[#1FB6C1]/40" : "hover:bg-white/[0.04] border border-transparent"
                }`}
                data-testid={`${testId}-opt-${ln.doc_id}-${ln.line_index}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-white truncate">{ln.description || ln.sku || "(untitled)"}</span>
                  <span className="text-[9px] font-mono text-[#647087] shrink-0">
                    {ln.doc_kind || "line"}
                    {ln.doc_number ? ` ${ln.doc_number}` : ""}
                  </span>
                </div>
                <div className="text-[9px] text-[#647087]">
                  {ln.sku ? `${ln.sku} · ` : ""}
                  {ln.quantity != null ? `qty ${ln.quantity}` : ""}
                  {ln.effective_amount != null ? ` · budget ZAR ${Number(ln.effective_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ""}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

